import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

type ModalStep = 'actions' | 'update-config';

interface UseBulkActionsModalStateParams {
  open: boolean;
  initialUpdateAttribute: string | null;
  availableAttributes: string[];
}

interface UseBulkActionsModalStateResult {
  step: ModalStep;
  setStep: (step: ModalStep) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedAttribute: string;
  setSelectedAttribute: (value: string) => void;
  editorValue: string;
  setEditorValue: (value: string) => void;
  multiSelectValues: string[];
  setMultiSelectValues: Dispatch<SetStateAction<string[]>>;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
  optionSearchValue: string;
  setOptionSearchValue: (value: string) => void;
  snoozeUntilValue: string;
  setSnoozeUntilValue: (value: string) => void;
  resetFormState: () => void;
  openUpdateConfig: (attributeKey: string) => void;
  resetToActions: () => void;
}

export function useBulkActionsModalState({
  open,
  initialUpdateAttribute,
  availableAttributes,
}: UseBulkActionsModalStateParams): UseBulkActionsModalStateResult {
  const [step, setStep] = useState<ModalStep>('actions');
  const [searchValue, setSearchValue] = useState('');
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const [editorValue, setEditorValue] = useState<string>('');
  const [multiSelectValues, setMultiSelectValues] = useState<string[]>([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [optionSearchValue, setOptionSearchValue] = useState('');
  const [snoozeUntilValue, setSnoozeUntilValue] = useState('');

  const resetFormState = useCallback(() => {
    setSelectedAttribute('');
    setEditorValue('');
    setMultiSelectValues([]);
    setSearchInputValue('');
    setOptionSearchValue('');
    setSnoozeUntilValue('');
    setSearchValue('');
  }, []);

  const openUpdateConfig = useCallback((attributeKey: string) => {
    setSearchValue('');
    setSelectedAttribute(attributeKey);
    setEditorValue('');
    setMultiSelectValues([]);
    setSearchInputValue('');
    setOptionSearchValue('');
    setSnoozeUntilValue('');
    setStep('update-config');
  }, []);

  const resetToActions = useCallback(() => {
    setStep('actions');
    resetFormState();
  }, [resetFormState]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (
      initialUpdateAttribute &&
      availableAttributes.some((attribute) => attribute === initialUpdateAttribute)
    ) {
      openUpdateConfig(initialUpdateAttribute);
      return;
    }

    resetToActions();
  }, [availableAttributes, initialUpdateAttribute, open, openUpdateConfig, resetToActions]);

  return {
    step,
    setStep,
    searchValue,
    setSearchValue,
    selectedAttribute,
    setSelectedAttribute,
    editorValue,
    setEditorValue,
    multiSelectValues,
    setMultiSelectValues,
    searchInputValue,
    setSearchInputValue,
    optionSearchValue,
    setOptionSearchValue,
    snoozeUntilValue,
    setSnoozeUntilValue,
    resetFormState,
    openUpdateConfig,
    resetToActions,
  };
}
